import {
    Component,
    EventEmitter,
    inject,
    Inject,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    NonNullableFormBuilder,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
    NzFormControlComponent,
    NzFormDirective,
    NzFormItemComponent,
    NzFormModule,
} from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import {
    NZ_MODAL_DATA,
    NzModalFooterDirective,
    NzModalRef,
    NzModalTitleDirective,
} from 'ng-zorro-antd/modal';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { LossReasonsService } from 'src/app/private/services/loss-reasons.service';
import { LossesService } from 'src/app/private/services/losses.service';
import { MeasurementunitsService } from 'src/app/private/services/measurementunits.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { FinalProductDto } from 'src/app/shared/dto/final-product.dto';
import { LossReasonDto } from 'src/app/shared/dto/loss-reason.dto';
import { LossDto } from 'src/app/shared/dto/loss.dto';
import { MeasurementUnitDto } from 'src/app/shared/dto/measurement-unit.dto';
import { MeasurementUnitResponseDto } from 'src/app/shared/dto/pagination/measurement-unit-response.dto';
import { RawMaterialDto } from 'src/app/shared/dto/rawMaterial.dto';
import { LossOrScrapSourceEnum } from 'src/app/shared/enums/loss-or-scrap-source.enum';
import { ConfigurationService } from 'src/app/private/services/configuration.service';
import { ConfigurationTypesEnum } from 'src/app/shared/enums/configuration-types.enum';

@Component({
    selector: 'app-modal-create-losses',
    templateUrl: './modal-create-losses.component.html',
    styleUrls: ['./modal-create-losses.component.scss'],
    standalone: true,
    imports: [
        NzModalTitleDirective,
        NzModalFooterDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzSelectComponent,
        NzOptionComponent,
        NzMessageModule,
        NzFormModule,
        ReactiveFormsModule,
        FormsModule,
        LoaderComponent,
        NzButtonModule
    ],
})
export class ModalCreateLossesComponent implements OnInit, OnDestroy {
    @Output() emitService = new EventEmitter();

    isLoading: boolean = true;
    isSaving: boolean = false;
    processId: number = 0;
    measurementUnits: MeasurementUnitDto[] = [];
    lossReasons: LossReasonDto[] = [];
    LossSourceEnum = LossOrScrapSourceEnum;
    private readonly defaultLossUnitConfigKey = 'productionOrders.DefaultUnitLoss';

    validateForm: FormGroup<{
        observations: FormControl<string>;
        quantity: FormControl<number>;
        rawMaterialSelected: FormControl<number | null>;
        finalProductSelected: FormControl<number | null>;
        selectedMeasurementUnitId: FormControl<number | null>;
        selectedReason: FormControl<number | null>;
    }>;

    private unsubscribe$ = new Subject<void>();

    private readonly modalRef = inject(NzModalRef);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly message = inject(NzMessageService);
    private readonly lossesService = inject(LossesService);
    private readonly measurementUnitsService = inject(MeasurementunitsService);
    private readonly lossReasonsService = inject(LossReasonsService);
    private readonly configurationService = inject(ConfigurationService);
    private readonly fb = inject(NonNullableFormBuilder);

    constructor(
        @Inject(NZ_MODAL_DATA)
        public data: {
            nzTitle: string;
            processId: number;
            origin: LossOrScrapSourceEnum;
            rawMaterials: RawMaterialDto[];
            finalProducts: FinalProductDto[];
        },
    ) {
        this.validateForm = this.fb.group({
            observations: [''],
            quantity: [0, [Validators.required, Validators.min(0.1)]],
            rawMaterialSelected: new FormControl<number | null>(null),
            finalProductSelected: new FormControl<number | null>(null),
            selectedMeasurementUnitId: new FormControl<number | null>(null, [
                Validators.required
            ]),
            selectedReason: new FormControl<number | null>(null, [
                Validators.required
            ]),
        });
    }

    async ngOnInit() {
        if (this.data.finalProducts.length == 1) {
            this.validateForm.controls['finalProductSelected'].setValue(
                this.data.finalProducts[0].id!
            );
        }

        await this.getLossReasons();

        this.processId = this.data?.processId as number;
        this.measurementUnits = await this.getMeasurementUnits();
        await this.setDefaultMeasurementUnit();
        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.lossReasonsService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    async submitForm(): Promise<void> {
        try {
            this.isSaving = true;

            if (!this.validateForm.valid) {
                this.isSaving = false;
                this.message.error(`Porfavor, rellene los campos obligatorios`);
                return;
            }

            const loss: LossDto = {
                observations: this.validateForm.value.observations ?? '',
                quantity: this.validateForm.value.quantity ?? 0,
                productionOrderProcessId: this.processId,
                measurementUnitId: this.validateForm.value.selectedMeasurementUnitId!,
                createdById: this.userLoggedService.userLogged.id,
                rawMaterialId: this.validateForm.value.rawMaterialSelected || undefined,
                finalProductId: this.validateForm.value.finalProductSelected || undefined,
                lossReasonId: this.validateForm.value.selectedReason!
            };

            const createdLoss: LossDto = await this.lossesService.create(loss);

            this.isSaving = false;
            this.emitService.emit(createdLoss);
            this.modalRef.close();
        } catch (e: any) {
            this.isSaving = false;
            this.message.error(e.message);
        }
    }

    handleCancel() {
        this.modalRef.close();
    }

    private async setDefaultMeasurementUnit(): Promise<void> {
        if (this.validateForm.controls['selectedMeasurementUnitId'].value) {
            return;
        }

        if (!this.measurementUnits.length) {
            return;
        }

        try {
            const configuration = await this.configurationService.getConfigurationByKey(this.defaultLossUnitConfigKey);

            if (configuration.type !== ConfigurationTypesEnum.NUMBER) {
                this.message.warning('La configuración de unidad de merma no es numérica.');
                return;
            }

            const defaultUnitId = Number(configuration.value);
            const defaultUnit = this.measurementUnits.find(unit => unit.id === defaultUnitId);

            if (!defaultUnit) {
                return;
            }

            this.validateForm.controls['selectedMeasurementUnitId'].setValue(defaultUnit.id);
        } catch (e: any) {
            this.message.warning(`No se pudo cargar la unidad de medida por defecto para las mermas: ${e.message}`);
        }
    }

    private async getMeasurementUnits(): Promise<MeasurementUnitDto[]> {
        try {
            const measurementsResponse: MeasurementUnitResponseDto =
                await this.measurementUnitsService.findAll();

            return measurementsResponse.measurementUnits;
        } catch (e: any) {
            this.message.error(`${e.message}`);

            return [];
        }
    }

    private async getLossReasons() {
        try {
            await this.lossReasonsService.setupWebSocket();
            this.lossReasonsService.lossReasons$
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe({
                    next: (lossReasons) => {
                        this.lossReasons = lossReasons;
                    },
                    error: () => {
                        this.message.error(
                            'No se han podido cargar las razones de merma.'
                        );
                    },
                });
        } catch (e: unknown) {
            if (e instanceof Error) {
                this.message.error(e.message);
            }
        }
    }
}
